import { useState, type FC } from "react";
import { HiChevronUp, HiChevronDown } from "react-icons/hi";
import type { HeatmapMetric } from "../../config/heatmap-metrics";
import { METRIC_CONFIGS } from "../../config/heatmap-metrics";

export interface HeatmapLegendProps {
  activeMetric: HeatmapMetric;
  className?: string;
}

export const HeatmapLegend: FC<HeatmapLegendProps> = ({
  activeMetric,
  className = "",
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const config = METRIC_CONFIGS[activeMetric];
  const { legend } = config;

  return (
    <div
      aria-label="Kartenlegende"
      className={`rounded-xl bg-white/95 p-3 shadow-lg backdrop-blur-md transition-all duration-200 dark:bg-gray-800/95 dark:border dark:border-gray-700/60 ${className}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-bold text-gray-800 dark:text-gray-100">
            {legend.title}
          </span>
          {legend.unit && (
            <span className="text-[10px] font-medium text-gray-400 dark:text-gray-400">
              ({legend.unit})
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          title={isCollapsed ? "Legende ausklappen" : "Legende einklappen"}
          aria-expanded={!isCollapsed}
        >
          {isCollapsed ? (
            <HiChevronUp className="h-3.5 w-3.5" />
          ) : (
            <HiChevronDown className="h-3.5 w-3.5" />
          )}
        </button>
      </div>

      {!isCollapsed && (
        <div className="mt-2 pt-1 border-t border-gray-100 dark:border-gray-700/50">
          {/*
            All metrics use step (bucketed) paint ramps, so the legend is always
            a discrete swatch list. A blended gradient bar would misrepresent
            the hard bucket edges the map actually draws.
          */}
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 pt-0.5">
            {legend.stops.map((stop, idx) => (
              <div key={idx} className="flex items-center gap-1.5">
                <span
                  className="h-2.5 w-2.5 flex-shrink-0 rounded-full ring-1 ring-black/10 dark:ring-white/20"
                  style={{ backgroundColor: stop.color }}
                />
                <span className="text-[10px] font-medium text-gray-600 dark:text-gray-300">
                  {stop.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
